import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "modalContent", "modalTitle", "modalDate"]

  connect() {
    // Close modal on escape key
    this.escapeHandler = (e) => {
      if (e.key === "Escape") {
        this.close()
      }
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
  }

  open(event) {
    event.preventDefault()
    const button = event.currentTarget

    // Get data from button
    const date = button.dataset.date
    const dateFormatted = button.dataset.dateFormatted
    const tasksJson = button.dataset.tasks

    // Parse tasks
    const tasks = JSON.parse(tasksJson)

    // Update modal content
    this.modalDateTarget.textContent = dateFormatted
    // views.* keys are not in the client-side store (only javascript.*), so use our own plural key.
    this.modalTitleTarget.textContent = t("javascript.controllers.calendar_modal_controller.task_count", { count: tasks.length })

    // Build task list HTML
    const taskListHtml = tasks.map(task => this.renderTask(task)).join('')
    this.modalContentTarget.innerHTML = taskListHtml

    // Show modal
    this.modalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    document.addEventListener("keydown", this.escapeHandler)

    // Focus trap
    setTimeout(() => {
      const firstFocusable = this.modalTarget.querySelector('a, button')
      if (firstFocusable) firstFocusable.focus()
    }, 100)
  }

  close() {
    this.modalTarget.classList.add('hidden')
    document.body.style.overflow = ''
    document.removeEventListener("keydown", this.escapeHandler)
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  renderTask(task) {
    // Priority color mapping
    const priorityColors = {
      emergency: 'bg-red-600 text-white border-red-500',
      critical: 'bg-rose-500 text-white border-rose-400',
      urgent: 'bg-orange-500 text-white border-orange-400',
      high: 'bg-yellow-500 text-black border-yellow-400',
      normal: 'bg-blue-500 text-white border-blue-400',
      low: 'bg-gray-500 text-white border-gray-400'
    }

    const statusBadges = {
      in_progress: 'bg-amber-400 text-black',
      assigned: 'bg-indigo-400 text-black',
      pending: 'bg-gray-600 text-white',
      on_hold: 'bg-fuchsia-500 text-white',
      completed: 'bg-green-600 text-white',
      cancelled: 'bg-slate-600 text-white',
      deferred: 'bg-slate-400 text-black'
    }

    const color = priorityColors[task.priority] || 'bg-gray-500 text-white border-gray-400'
    const statusBadge = statusBadges[task.status] || 'bg-gray-500 text-white'
    const dim = task.status === 'completed' ? 'opacity-70' : ''
    const strike = task.status === 'completed' ? 'line-through' : ''

    return `
      <a href="/companies/${task.company_id}/tasks/${task.id}" 
         class="block ${color} ${dim} rounded-lg px-4 py-3 border-l-4 hover:brightness-110 transition-all group">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="font-semibold text-sm mb-1 ${strike}">${this.escapeHtml(task.title)}</div>
            <div class="flex flex-wrap items-center gap-2 text-xs opacity-90">
              <span class="flex items-center gap-1">
                <span class="material-symbols-outlined !text-sm">person</span>
                ${this.escapeHtml(task.assignee || t('javascript.controllers.calendar_modal_controller.unassigned'))}
              </span>
              ${task.asset ? `
                <span class="flex items-center gap-1">
                  <span class="material-symbols-outlined !text-sm">inventory_2</span>
                  ${this.escapeHtml(task.asset)}
                </span>
              ` : ''}
              ${task.task_type ? `
                <span class="flex items-center gap-1">
                  <span class="material-symbols-outlined !text-sm">category</span>
                  ${this.escapeHtml(task.task_type)}
                </span>
              ` : ''}
            </div>
          </div>
          <div class="flex flex-col items-end gap-2 shrink-0">
            <span class="px-2 py-0.5 rounded text-[10px] font-bold ${statusBadge}">
              ${this.escapeHtml(task.status_label)}
            </span>
            <span class="text-xs opacity-80 flex items-center gap-1">
              <span class="material-symbols-outlined !text-xs">schedule</span>
              ${this.escapeHtml(task.due_time || t('javascript.controllers.calendar_modal_controller.no_time'))}
            </span>
          </div>
        </div>
        ${task.description ? `
          <div class="mt-2 text-xs opacity-80 line-clamp-2">
            ${this.escapeHtml(task.description)}
          </div>
        ` : ''}
      </a>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

